import { i18n } from '@lingui/core'
import { Trans, t } from '@lingui/macro'
import classNames from 'classnames'
import { useEffect } from 'react'

import { useRemoveVoucher } from './useRemoveVoucher'
import styles from './vouchers.module.scss'
import ApplyVoucherButton from '../account-dashboard-page/AccountDashboardPageLayout/justRewards/Vouchers/ApplyVoucherButton'
import { CustomerCoupon } from '../account-dashboard-page/AccountDashboardPageLayout/justRewards/Vouchers/customerVouchers.query'
import RemoveVoucherButton from '../account-dashboard-page/AccountDashboardPageLayout/justRewards/Vouchers/RemoveVoucherButton'
import { useCartItems } from '../cart/CartPage/useCartItems'
import { PRIVILEGES_ENABLED } from '../constants'
import VoucherEuroIcon from '../core/VoucherEuroIcon'
import Loader from '../presentation/Loader'
import Price from '../presentation/Price'
import { ProgressBarLoyalty } from '../progress/ProgressBarLoyalty'
import { useToaster } from '../toaster'
import Heading from '../typography/Heading'
import Text from '../typography/Text'
import parseMagentoDateTime from '../utils/parseMagentoDateTime'
import { M2ApiResponseError } from '../utils/RestApi'

export interface VoucherProps {
    className?: string
    showButtons?: boolean
    voucher: CustomerCoupon
}

export const Voucher = ({ className, voucher, showButtons }: VoucherProps) => {
    const { isLoading: isRemovingVoucher, removeVoucher } = useRemoveVoucher()

    const { totalItemsExclGifts: quantity, prices } = useCartItems()

    const { toast } = useToaster()

    const {
        minimalOrderAmount: voucherMinimalOrderAmount,
        applied: voucherApplied,
        code,
        amount,
        validUntil,
        type: { image },
        valuePercentage,
    } = voucher

    const subTotal = prices?.subtotalIncludingTax?.value ?? 0
    const minimalOrderAmount = voucherMinimalOrderAmount?.value || 0
    const insufficientAmount = minimalOrderAmount - subTotal
    const isCompleted = insufficientAmount <= 0

    const remainingAmount = <Price price={insufficientAmount ?? 0} />

    useEffect(() => {
        if (voucherApplied && insufficientAmount > 0) {
            removeVoucher(code)
        }
    }, [voucherApplied, code, removeVoucher, insufficientAmount])

    const discountValue = amount.value ? (
        <Price
            className={styles.price}
            price={amount.value}
            currency={amount.currency}
        />
    ) : valuePercentage ? (
        `${valuePercentage}&#37;}`
    ) : (
        ''
    )

    const minimumSpendValue = (
        <Price
            className={styles.price}
            price={
                (voucherMinimalOrderAmount?.value ||
                    voucher.originalMinimalOrderAmount) ??
                0
            }
            currency={voucherMinimalOrderAmount?.currency}
        />
    )

    const onApplyVoucherSuccess = () => {
        const message =
            (quantity ?? 0) > 0
                ? i18n._(
                      t({
                          id: 'user.rewards.appliedToCart',
                          message: `Your discount is applied to your cart`,
                      }),
                  )
                : i18n._(
                      t({
                          id: 'user.rewards.appliedToEmptyCart',
                          message: `Your discount will be automatically applied when you add items to your shoppingcart`,
                      }),
                  )
        toast.success(message)
    }

    const handleOnApplyVoucherSuccess = () => {
        onApplyVoucherSuccess()
    }
    const handleOnRemoveVoucherSuccess = () => {
        toast.success(
            i18n._(
                t({
                    id: 'user.rewards.voucherRemoved',
                    message: `The voucher is removed from your cart`,
                }),
            ),
        )
    }

    const handleOnApplyVoucherError = (err: any) => {
        if (!(err instanceof Error)) {
            return
        }
        let errorMessage = err.message
        if (err instanceof M2ApiResponseError) {
            errorMessage = err.userMessage
        }
        toast.error(errorMessage)
    }

    return (
        <div
            className={classNames(
                styles.voucher,
                PRIVILEGES_ENABLED && styles.privileges,
                className,
            )}
        >
            <div className={styles.row}>
                <div className={classNames(styles.col, styles.icon)}>
                    {image ? (
                        <figure className={styles.imageContainer}>
                            <img className={styles.image} src={image} />
                        </figure>
                    ) : (
                        <VoucherEuroIcon />
                    )}
                </div>

                {isRemovingVoucher ? (
                    <div className={styles.row}>
                        <div className={styles.col}>
                            <Loader className={styles.loader} />
                        </div>
                        <div className={styles.col}>Removing voucher...</div>
                    </div>
                ) : (
                    <div className={styles.col}>
                        <Heading variant="h3" element="h2">
                            {voucher.description}
                        </Heading>
                        <Text>
                            <Trans id="user.rewards.receiveDiscount">
                                Receive a {discountValue} discount with a
                                minimum spend of {minimumSpendValue}
                            </Trans>
                        </Text>
                        <Text color="grey">
                            <Trans id="user.rewards.validUntil">
                                Valid until:
                            </Trans>{' '}
                            <span>
                                {validUntil ? (
                                    new Intl.DateTimeFormat(i18n.locale, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                    }).format(
                                        new Date(
                                            parseMagentoDateTime(validUntil),
                                        ),
                                    )
                                ) : (
                                    <Trans id="loyalty.sidePanel.validUntilIndefinitely">
                                        N/A
                                    </Trans>
                                )}
                            </span>
                        </Text>
                        {isCompleted && showButtons && (
                            <div
                                className={classNames(
                                    styles.row,
                                    styles.buttons,
                                )}
                            >
                                <ApplyVoucherButton
                                    className={styles.applied}
                                    variant="secondary"
                                    voucher={voucher}
                                    minWidth
                                    onApply={handleOnApplyVoucherSuccess}
                                    onApplyError={handleOnApplyVoucherError}
                                />
                                {voucher.isActive && (
                                    <RemoveVoucherButton
                                        variant="link"
                                        onRemove={handleOnRemoveVoucherSuccess}
                                        minWidth
                                        code={voucher.code}
                                    >
                                        <Trans id="user.rewards.removeVoucherButton">
                                            Remove
                                        </Trans>
                                    </RemoveVoucherButton>
                                )}
                            </div>
                        )}
                        {!voucherApplied && (
                            <ProgressBarLoyalty
                                showProgress
                                insufficientAmount={insufficientAmount}
                                isCompleted={isCompleted}
                            >
                                {isCompleted ? (
                                    <Trans id="cart.gift.rule.completed">
                                        Completed
                                    </Trans>
                                ) : (
                                    <Trans id="cart.nextGiftRule.priceText">
                                        {remainingAmount} to go
                                    </Trans>
                                )}
                            </ProgressBarLoyalty>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
